import Config from '../../config';
import { FeedActions, getFeedActions } from './FeedState';
import { NotificationActions } from '../global/actions';
import { FeedSpread } from '@tradingblock/types';
import { isSpread, WebsocketFeedParser, isOCCSymbol, toRawOccSymbol } from '@tradingblock/api';
import _ from 'lodash';
import { batch } from 'react-redux';

// Spreads batching worker (initialized when PERF batching is enabled)
let spreadsWorker: Worker | null = null;
let hasSetupSpreadsWorkerListener = false;
// Stored interval from dashboard settings (used when creating worker)
let spreadsWorkerIntervalMs: number | null = null;
// Last SpreadTime (as ms) sent to Redux per symbol - only dispatch if newer
const lastSpreadTimeSentToRedux = new Map<string, number>();

function toTimestamp(val: string | number | null | undefined): number {
  if (val == null) return 0;
  if (typeof val === 'number') return val;
  const parsed = new Date(val).getTime();
  return isNaN(parsed) ? 0 : parsed;
}

// JSON Channel message interface for spreads
interface JsonSpreadMessage {
  ts: string;
  id: string;
  l: number;
  b: number;
  a: number;
  c: number;
}

// SpreadsWebSocketService.ts
class SpreadsWebSocketService {
  private static instance: SpreadsWebSocketService;
  private ws: WebSocket | null = null;
  private lastMessageTime: number = 0;
  private dataFlowCheckInterval: NodeJS.Timeout | null = null;
  private onDataFlowStopped: (() => void) | null = null;
  private hasSubscriptions: boolean = false;
  private readonly DATA_FLOW_TIMEOUT_MS = 30000; // 30 seconds without data = consider stopped

  private constructor() {}

  // Parse JSON channel message and convert directly to FeedSpread object
  // JSON format: {"ts": "11/19/2025 21:40:51Z", "id": "20032788", "l": 0.51, "b": 0.50, "a": 0.53, "c": -0.3150}
  private parseJsonSpread(jsonData: string | object): FeedSpread | null {
    try {
      let jsonMessage: JsonSpreadMessage;

      if (typeof jsonData === 'string') {
        // Check if it's already a FIX format string (contains pipe separator)
        if (jsonData.includes('|')) {
          return null; // Let FIX parser handle it
        }
        jsonMessage = JSON.parse(jsonData);
      } else {
        jsonMessage = jsonData as JsonSpreadMessage;
      }

      // Validate required fields (all fields are required per spec)
      if (
        !jsonMessage.ts ||
        !jsonMessage.id ||
        jsonMessage.l == null ||
        jsonMessage.b == null ||
        jsonMessage.a == null ||
        jsonMessage.c == null
      ) {
        return null;
      }

      // Process symbol (remove dots and handle OCC symbols, same as FIX parser)
      const symbol = jsonMessage.id.replace(/\./g, '');
      const Symbol = isOCCSymbol(symbol) ? toRawOccSymbol(symbol) : symbol;

      // Convert JSON directly to FeedSpread object
      const spread: FeedSpread = {
        SpreadTime: jsonMessage.ts,
        Symbol,
        Last: jsonMessage.l,
        Bid: jsonMessage.b,
        Ask: jsonMessage.a,
        Change: jsonMessage.c,
      };

      return spread;
    } catch (error) {
      // If JSON parsing fails, return null to fall back to FIX parser
      return null;
    }
  }

  // Singleton instance access
  public static getInstance(): SpreadsWebSocketService {
    if (!SpreadsWebSocketService.instance) {
      SpreadsWebSocketService.instance = new SpreadsWebSocketService();
      console.log('new spreads websocket instance created');
    }
    return SpreadsWebSocketService.instance;
  }

  // Set callback for when data flow stops
  public setDataFlowStoppedCallback(callback: (() => void) | null) {
    this.onDataFlowStopped = callback;
  }

  // Set whether subscriptions exist (only monitor when subscriptions are active)
  public setHasSubscriptions(hasSubscriptions: boolean) {
    this.hasSubscriptions = hasSubscriptions;
    if (hasSubscriptions && this.ws && this.ws.readyState === WebSocket.OPEN) {
      // Start monitoring if websocket is open and we have subscriptions
      this.startDataFlowMonitoring();
    } else if (!hasSubscriptions) {
      // Stop monitoring if no subscriptions
      this.stopDataFlowMonitoring();
    }
  }

  // Start monitoring data flow (only if subscriptions exist)
  private startDataFlowMonitoring() {
    // Don't start monitoring if there are no subscriptions
    if (!this.hasSubscriptions) {
      return;
    }

    if (this.dataFlowCheckInterval) {
      clearInterval(this.dataFlowCheckInterval);
    }
    this.lastMessageTime = Date.now();
    this.dataFlowCheckInterval = setInterval(() => {
      // Double-check subscriptions before triggering fallback
      if (!this.hasSubscriptions) {
        this.stopDataFlowMonitoring();
        return;
      }
      const timeSinceLastMessage = Date.now() - this.lastMessageTime;
      if (timeSinceLastMessage > this.DATA_FLOW_TIMEOUT_MS && this.ws && this.ws.readyState === WebSocket.OPEN) {
        console.warn('Spreads WebSocket: No data received for', timeSinceLastMessage, 'ms, triggering fallback');
        if (this.onDataFlowStopped) {
          this.onDataFlowStopped();
        }
      }
    }, 5000); // Check every 5 seconds
  }

  // Stop monitoring data flow
  private stopDataFlowMonitoring() {
    if (this.dataFlowCheckInterval) {
      clearInterval(this.dataFlowCheckInterval);
      this.dataFlowCheckInterval = null;
    }
  }

  // Initialize WebSocket connection
  public async connect(
    dispatch: React.Dispatch<FeedActions>,
    dispatcher: any,
    globalDispatch: any,
    apiToken: string,
    fullDispatcher?: any
  ) {
    try {
      if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
        this.ws = new WebSocket(Config.tradingApiWebsocketsNew + '/spreads?token=' + apiToken + '&format=JSON');

        this.ws.onopen = () => {
          dispatcher.connected();
          getFeedActions(dispatch).setSpreadsFeedStatus('open');
          this.lastMessageTime = Date.now();
          // Only start monitoring if subscriptions exist
          if (this.hasSubscriptions) {
            this.startDataFlowMonitoring();
          }
          if (Config.perf && Config.perf.batchedQuotes && typeof Worker !== 'undefined') {
            const publicUrl = process.env.PUBLIC_URL || '';
            if (!spreadsWorker) {
              try {
                spreadsWorker = new Worker(`${publicUrl}/spreadsWorker.js`);
                // Use stored interval from dashboard settings if available, otherwise fall back to config
                const intervalMs =
                  spreadsWorkerIntervalMs !== null ? spreadsWorkerIntervalMs : Config.perf.quotesBatchIntervalMs;
                spreadsWorker.postMessage({ type: 'CONFIG', intervalMs });
              } catch (error) {
                console.error('Failed to create spreadsWorker:', error);
              }
            }
            if (spreadsWorker && !hasSetupSpreadsWorkerListener && fullDispatcher) {
              spreadsWorker.onmessage = (ev: MessageEvent) => {
                const data = ev.data as any;
                if (data && data.type === 'SPREAD_DELTAS' && data.spreads) {
                  const spreads = data.spreads;
                  const filtered: Record<string, FeedSpread> = {};
                  for (const symbol of Object.keys(spreads)) {
                    const spread = spreads[symbol];
                    const spreadTs = toTimestamp(spread.SpreadTime);
                    const lastTs = lastSpreadTimeSentToRedux.get(symbol);
                    const lastTsVal = lastTs != null ? lastTs : 0;
                    if (spreadTs > lastTsVal) {
                      filtered[symbol] = spread;
                      lastSpreadTimeSentToRedux.set(symbol, spreadTs);
                    }
                  }
                  if (Object.keys(filtered).length > 0) {
                    batch(() => fullDispatcher.feed.updateSpreads(filtered));
                  }
                }
              };
              hasSetupSpreadsWorkerListener = true;
            }
          }
          globalDispatch(
            NotificationActions.read({
              status: 'Error',
              title: 'Spreads WebSocket Error',
              message: '',
              global: false,
              time: new Date(),
              key: 'spreads-feed-error',
              read: false,
              hide: false,
              viewed: false,
            })
          );
        };

        this.ws.onmessage = (ev: MessageEvent) => {
          const val = ev.data;

          // Update last message time for data flow monitoring
          this.lastMessageTime = Date.now();

          // Try to parse as JSON first
          const jsonSpread = this.parseJsonSpread(val);

          if (jsonSpread !== null) {
            // Extract only FeedSpread properties to ensure consistency with FIX path
            const spread: FeedSpread = {
              SpreadTime: jsonSpread.SpreadTime,
              Symbol: jsonSpread.Symbol,
              Last: jsonSpread.Last,
              Bid: jsonSpread.Bid,
              Ask: jsonSpread.Ask,
              Change: jsonSpread.Change,
            };
            const rawValue = typeof val === 'string' ? val : JSON.stringify(val);
            if (Config.perf && Config.perf.batchedQuotes && spreadsWorker) {
              spreadsWorker.postMessage({ type: 'SPREAD', spread: spread });
            } else {
              // Add to FeedState queue for FeedSync processing
              getFeedActions(dispatch).onSpread(spread, rawValue);
            }
          } else {
            // Fall back to FIX format parser (original implementation)
            const parser = new WebsocketFeedParser(val);
            const msg = parser.parse();

            if (_.isNil(msg)) {
              console.warn('unknown feed type', val);
            } else if (!msg.isValid) {
              console.warn('errored feed message', val);
            } else if (isSpread(msg)) {
              if (Config.perf && Config.perf.batchedQuotes && spreadsWorker) {
                spreadsWorker.postMessage({ type: 'SPREAD', spread: msg });
              } else {
                // Add to FeedState queue for FeedSync processing
                getFeedActions(dispatch).onSpread(msg, val);
              }
            }
          }
        };

        this.ws.onclose = (ev: CloseEvent) => {
          this.stopDataFlowMonitoring();
          if (ev.reason) {
            getFeedActions(dispatch).setSpreadsCloseReason(ev.reason);
            console.warn(ev.reason);
          }
          getFeedActions(dispatch).setSpreadsFeedStatus('closed');
          // Notification will be handled by initializer based on both websockets status
        };

        this.ws.onerror = error => {
          this.stopDataFlowMonitoring();
          dispatcher.errored({ error: error, reason: 'Unable to connect', message: 'Datafeed error' });
          getFeedActions(dispatch).setSpreadsFeedStatus('error');
          console.error('Spreads WebSocket error:', error);
          // Notification will be handled by initializer based on both websockets status
        };
      }
    } catch (createWebsocketError) {
      dispatcher.errored({
        error: createWebsocketError,
        reason: 'Error creating spreads websocket',
        message: 'Spreads Websocket error',
      });
    }
  }
  // Send message to WebSocket
  public sendMessage(data: string | ArrayBufferLike | Blob | ArrayBufferView) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(data);
    } else if (this.ws) {
      console.warn('trying to send message to closed/closing websocket', data);
    }
  }

  public shouldTryReconnect = () => {
    if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
      return true;
    }
    return false;
  };

  // Close WebSocket connection
  public closeConnection() {
    this.stopDataFlowMonitoring();
    if (this.ws) {
      this.ws.close();
    }
  }

  // Get last message time for monitoring
  public getLastMessageTime(): number {
    return this.lastMessageTime;
  }

  // Update the worker's flush interval from dashboard settings
  public updateWorkerInterval(intervalMs: number): void {
    if (typeof intervalMs === 'number' && intervalMs > 0) {
      // Store the interval so it's used when worker is created
      spreadsWorkerIntervalMs = intervalMs;
      // Update the worker if it exists
      if (spreadsWorker) {
        spreadsWorker.postMessage({ type: 'CONFIG', intervalMs });
      }
    }
  }
}

export const spreadsWebSocketService = SpreadsWebSocketService.getInstance();
